import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { FormattedTag, LoyaltyProgram as LoyaltyProgramCore } from 'core/components';
import { getStaticUrl } from 'core/helpers';
import { withUser, withImagesData, withCoreComponent } from 'core/hocs';
import { Main } from 'components/main/main';
import { Carousel } from 'components/carousel/carousel';
import { HeadHelmet } from 'components/head-helmet/head-helmet';
import { VipItemCard } from 'components/vip/vip-item-card';
import { VipTable } from 'components/vip/vip-table';
import { PAGE_NAMES } from '../../constants';

import './vip.scss';

const carouselSettings = {
  arrows: true,
  dots: true,
  responsive: [
    {
      breakpoint: 719,
      settings: {
        arrows: false,
        slidesToShow: 1.05,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 1024,
      settings: {
        arrows: false,
        slidesToShow: 2.35,
        slidesToScroll: 1,
      },
    },
  ],
};

const staticUrl = getStaticUrl();

class VipUI extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    userData: PropTypes.shape().isRequired,
    defaultCurrency: PropTypes.string.isRequired,
    imagesData: PropTypes.shape().isRequired,
    loyaltyLevels: PropTypes.arrayOf(PropTypes.shape()),
  }

  static defaultProps = {
    loyaltyLevels: [],
  }

  getLevels = () => {
    const {
      intl,
      userData: {
        currency,
      },
      defaultCurrency,
    } = this.props;

    const targetCurrency = currency || defaultCurrency;

    const itemLevel1 = JSON.parse(`{${intl.formatMessage({ id: 'vip.level.1.data' }, { currency: targetCurrency })}}`);
    const itemLevel2 = JSON.parse(`{${intl.formatMessage({ id: 'vip.level.2.data' }, { currency: targetCurrency })}}`);
    const itemLevel3 = JSON.parse(`{${intl.formatMessage({ id: 'vip.level.3.data' }, { currency: targetCurrency })}}`);
    const itemLevel4 = JSON.parse(`{${intl.formatMessage({ id: 'vip.level.4.data' }, { currency: targetCurrency })}}`);
    const itemLevel5 = JSON.parse(`{${intl.formatMessage({ id: 'vip.level.5.data' }, { currency: targetCurrency })}}`);

    const levels = [
      itemLevel1,
      itemLevel2,
      itemLevel3,
      itemLevel4,
      itemLevel5
    ];

    return levels;
  }

  render() {
    const {
      imagesData,
      loyaltyLevels
    } = this.props;
    const getVipCMSImage = order =>
      imagesData.VIP_DESCRIPTION.find(img => img?.entityId?.toString() === order.toString())?.imgUrl;

    const accountManagerIcon = getVipCMSImage(1) || `${staticUrl}/vip/account-manager.png`;
    const higherLimitsIcon = getVipCMSImage(2) || `${staticUrl}/vip/higher-limits.png`;
    const exclusivePromotionsIcon = getVipCMSImage(3) || `${staticUrl}/vip/exclusive-promotions.png`;

    return (
      <Main className="vip-page">
        <HeadHelmet
          page={PAGE_NAMES.VIP}
          options={{ indexing: false }}
        />

        <div className="vip-page-bg" />

        <div className="text-center mt-md-5">
          <FormattedTag tag="h1" id="vip.main" className="font-weight-strong text-capitalize mt-4_75 mt-md-8_5 mb-1_25 letter-spacing-default title-main font-family-oswald" />
          <FormattedTag id="vip.secondary" className="text-white title-secondary mx-auto letter-spacing-default mt-md-2_25" />

          <div className="d-flex flex-column flex-md-row-reverse align-items-center">
            <img className="img-account-manager ml-5_5 ml-md-12 mt-0_75 mt-md-2_25" src={accountManagerIcon} alt="account-manager-icon" />

            <FormattedTag id="vip.manager" className="h2 text-capitalize letter-spacing-extra my-1 d-md-none" />

            <div className="d-md-flex flex-column align-items-end mt-md-8_5">
              <FormattedTag id="vip.manager" className="h2 text-capitalize letter-spacing-default mb-1_5 mt-1 d-none d-md-block font-family-oswald" />
              <FormattedTag id="vip.manager.desc" className="text-white mx-auto mx-md-0 title-secondary-desc letter-spacing-default text-md-right" />
            </div>
          </div>

          <div className="d-flex flex-column align-items-center flex-md-row">
            <img className="img-higher-limits mt-3_25 mt-md-2 ml-md-8" src={higherLimitsIcon} alt="higher-limits-icon" />

            <FormattedTag id="vip.limits" className="h2 text-capitalize letter-spacing-extra my-1 d-md-none" />

            <div className="d-md-flex flex-column align-items-start ml-md-8 mt-md-2_25">
              <FormattedTag id="vip.limits" className="h2 text-capitalize letter-spacing-default mb-1_5 mt-1 d-none d-md-block font-family-oswald" />
              <FormattedTag id="vip.limits.desc" className="text-white mx-auto mx-md-0 text-md-left title-secondary-desc letter-spacing-default" />
            </div>
          </div>

          <div className="d-flex flex-column align-items-center flex-md-row-reverse">
            <img className="img-exclusive-promotions mb-0_5 mt-3_25 mt-md-3 mr-md-1" src={exclusivePromotionsIcon} alt="exclusive-promotions-icon" />

            <FormattedTag id="vip.promo" className="h2 text-capitalize letter-spacing-extra my-1 d-md-none" />

            <div className="d-md-flex flex-column align-items-end mr-md-4 mt-md-1_5">
              <FormattedTag id="vip.promo" className="h2 text-capitalize letter-spacing-default mb-1_25 mt-1 d-none d-md-block font-family-oswald" />
              <FormattedTag id="vip.promo.desc" className="text-white mx-auto mx-md-0 text-md-right title-secondary-desc letter-spacing-default" />
            </div>
          </div>

          <Carousel className="vip-items-carousel mt-5 d-md-none mr-n2 mr-sm-0" settings={carouselSettings}>
            {this.getLevels().map((item, idx) => (
              <VipItemCard key={item.xp} level={item} sortIndex={idx + 1} />
            ))}
          </Carousel>

          <div className="table-desktop-container d-none d-md-block rounded overflow-hidden mt-md-8_5">
            <VipTable levels={this.getLevels()} loyaltyLevels={loyaltyLevels} />
          </div>

        </div>
      </Main>
    );
  }
}

export default withCoreComponent(
  LoyaltyProgramCore,
  withImagesData(withUser(injectIntl(VipUI)))
);
